// src/utils/mockDataNotificationBus.ts
// ─────────────────────────────────────────────────────────────────
// Unified Crew Notification Bus — Phase 1 (Mock Implementation)
//
// This is a module-level store using the same pattern as the
// existing roster push log in Roster.tsx:
//   _crewNotifications[] ← addCrewNotification() writes here
//   getCrewNotifications() ← MobileComms reads from here
//
// At beta (Phase 6), notificationBusService.ts swaps these
// module-level functions for API calls. No component changes.
//
// Source modules write notifications like this:
//   import { addCrewNotification } from '../utils/mockDataNotificationBus'
//   addCrewNotification({ type: 'SWMS_UPDATE', employeeIds: [...], ... })
//
// MobileComms reads via notificationBusService, not directly.
// ─────────────────────────────────────────────────────────────────

import type {
  CrewNotification, NotificationType, NotificationPriority,
  AccommodationPayload, IncidentAlertPayload, SWMSUpdatePayload,
  PermitToWorkPayload, ToolboxSignoffPayload, PlantDefectPayload,
  MaterialReadyPayload, ITPReleasePayload, TimesheetApprovedPayload,
} from '../types/mobileCrewTypes'

// ── Module-level store ─────────────────────────────────────────────
let _seq = 100
let _crewNotifications: CrewNotification[] = []

function nextId(): string {
  return `cn${String(++_seq).padStart(4, '0')}`
}

// ── Write function (called by source modules) ──────────────────────
export interface AddNotificationInput {
  type: NotificationType
  priority?: NotificationPriority
  employeeIds: string[]
  jobIds: string[]
  heading: string
  body: string
  requiredAction?: string
  attachmentUrl?: string
  sourceModule: string
  sourceRecordId?: string
  expiresAt?: string
  requiresAck?: boolean
  payload: Record<string, unknown>
}

export function addCrewNotification(input: AddNotificationInput): CrewNotification {
  const n: CrewNotification = {
    id:            nextId(),
    type:          input.type,
    priority:      input.priority ?? 'MEDIUM',
    status:        'SENT',
    employeeIds:   input.employeeIds,
    jobIds:        input.jobIds,
    heading:       input.heading,
    body:          input.body,
    requiredAction:input.requiredAction,
    attachmentUrl: input.attachmentUrl,
    sourceModule:  input.sourceModule,
    sourceRecordId:input.sourceRecordId,
    createdAt:     new Date().toISOString(),
    createdById:   'system',
    createdByName: 'System',
    expiresAt:     input.expiresAt,
    requiresAck:   input.requiresAck ?? true,
    acknowledgements: [],
    payload: input.payload,
  }
  _crewNotifications = [n, ..._crewNotifications]
  return n
}

// ── Read functions (called by notificationBusService) ─────────────
export interface GetNotificationsFilter {
  employeeIds?: string[]
  types?: NotificationType[]
  jobIds?: string[]
  unreadOnly?: boolean
  limit?: number
}

export function getCrewNotifications(filter?: GetNotificationsFilter): CrewNotification[] {
  let results = [..._crewNotifications]

  if (filter?.employeeIds?.length) {
    results = results.filter(n =>
      n.employeeIds.some(id => filter.employeeIds!.includes(id))
    )
  }
  if (filter?.types?.length) {
    results = results.filter(n => filter.types!.includes(n.type))
  }
  if (filter?.jobIds?.length) {
    results = results.filter(n =>
      n.jobIds.some(id => filter.jobIds!.includes(id))
    )
  }
  if (filter?.unreadOnly) {
    results = results.filter(n => n.status !== 'ACKNOWLEDGED')
  }
  if (filter?.limit) {
    results = results.slice(0, filter.limit)
  }

  return results
}

export function acknowledgeNotification(
  notificationId: string,
  employeeId: string,
  employeeName: string,
): CrewNotification | null {
  const idx = _crewNotifications.findIndex(n => n.id === notificationId)
  if (idx < 0) return null

  const n = { ..._crewNotifications[idx] }
  if (!n.acknowledgements.find(a => a.employeeId === employeeId)) {
    n.acknowledgements = [...n.acknowledgements, {
      employeeId,
      employeeName,
      acknowledgedAt: new Date().toISOString(),
    }]
  }
  // If all recipients have acknowledged, mark as ACKNOWLEDGED
  if (n.acknowledgements.length >= n.employeeIds.length) {
    n.status = 'ACKNOWLEDGED'
  }
  _crewNotifications = [
    ..._crewNotifications.slice(0, idx),
    n,
    ..._crewNotifications.slice(idx + 1),
  ]
  return n
}

export function getUnreadCount(employeeId?: string, types?: NotificationType[]): number {
  return _crewNotifications.filter(n => {
    const unread = n.status !== 'ACKNOWLEDGED'
    const forEmployee = !employeeId || n.employeeIds.includes(employeeId)
    const ofType = !types?.length || types.includes(n.type)
    return unread && forEmployee && ofType
  }).length
}

export function getNotificationById(id: string): CrewNotification | null {
  return _crewNotifications.find(n => n.id === id) ?? null
}

// ── Seed data — representative entries for all notification types ──
// Loaded at module initialisation so all tabs have display data
// from day one of Phase 1 development.

const SEED_EMPLOYEE_IDS = ['e1', 'e2', 'e3', 'e5', 'e7']
const TODAY = '2026-07-11'

const seedData: AddNotificationInput[] = [
  // Accommodation
  {
    type: 'ACCOMMODATION',
    priority: 'HIGH',
    employeeIds: ['e3', 'e5'],
    jobIds: ['j4'],
    heading: 'Accommodation assigned — Quest Broadbeach',
    body: 'Your accommodation for the Annerley HV project has been booked. Check-in Friday 12 July, check-out Monday 15 July.',
    requiredAction: 'Confirm receipt and check-in details.',
    sourceModule: 'Travel.tsx',
    sourceRecordId: 'accomm-001',
    requiresAck: true,
    payload: {
      propertyName: 'Quest Broadbeach',
      address: '2551 Gold Coast Hwy, Broadbeach QLD 4218',
      keyholderName: 'Dave Cooper',
      keyholderPhone: '0412 888 777',
      checkIn: '2026-07-12T14:00:00',
      checkOut: '2026-07-15T10:00:00',
      coOccupants: ['Sam Nguyen'],
      amenities: ['Kitchen', 'Laundry', 'Parking'],
      bookingRef: 'QB-2026-447',
    } as AccommodationPayload,
  },
  // SWMS Update
  {
    type: 'SWMS_UPDATE',
    priority: 'CRITICAL',
    employeeIds: ['e1', 'e7'],
    jobIds: ['j1', 'j2'],
    heading: 'SWMS updated — must re-read and re-sign before work',
    body: 'SWMS-2026-004 (Underground Cable Installation) has been updated to Rev 3. You must re-read and re-sign before commencing excavation work today.',
    requiredAction: 'Open the SWMS, read Rev 3 changes, and tap Sign to confirm.',
    sourceModule: 'SWMS.tsx',
    sourceRecordId: 'swms-004',
    requiresAck: true,
    payload: {
      swmsRef: 'SWMS-2026-004',
      swmsTitle: 'Underground Cable Installation',
      oldRevision: 'Rev 2',
      newRevision: 'Rev 3',
      changesSummary: 'Updated exclusion zone distances and added unmarked utilities strike procedure.',
      requiresReSign: true,
    } as SWMSUpdatePayload,
  },
  // Permit to Work
  {
    type: 'PERMIT_TO_WORK',
    priority: 'HIGH',
    employeeIds: ['e3'],
    jobIds: ['j4'],
    heading: 'Hot Work Permit approved — HW-2026-021',
    body: 'Hot Work Permit HW-2026-021 has been approved for 16:00–21:00 Thu 11 Jul at Annerley Rd site. Fire watch required.',
    requiredAction: 'Acknowledge receipt. Ensure fire watch is in place before commencing.',
    sourceModule: 'Permits.tsx',
    sourceRecordId: 'ptw-hw-2026-021',
    requiresAck: true,
    payload: {
      permitRef: 'HW-2026-021',
      permitType: 'Hot Work',
      siteAddress: 'Annerley Rd, Annerley QLD 4103',
      validFrom: `${TODAY}T16:00:00`,
      validTo: `${TODAY}T21:00:00`,
      conditions: ['Fire watch required throughout', 'Fire extinguisher within 3m', 'No combustible materials within 2m'],
      emergencyContacts: [
        { name: 'Mike Walsh', role: 'PM', phone: '0421 234 567' },
        { name: 'Energex Emergency', role: 'Network Operator', phone: '13 62 62' },
      ],
    } as PermitToWorkPayload,
  },
  // Toolbox Sign-off
  {
    type: 'TOOLBOX_SIGNOFF',
    priority: 'MEDIUM',
    employeeIds: ['e1', 'e2', 'e5'],
    jobIds: ['j1'],
    heading: 'Toolbox sign-off required — Working at Heights Refresher',
    body: 'Toolbox Talk: Working at Heights Refresher was held on 10 Jul 2026, presented by Jane Smith. Please confirm you have read the summary and understood the content.',
    requiredAction: 'Tap Sign Off to record your acknowledgement.',
    sourceModule: 'Toolbox.tsx',
    sourceRecordId: 'tb-2026-0018',
    requiresAck: true,
    payload: {
      toolboxRef: 'TB-2026-0018',
      topic: 'Working at Heights Refresher',
      presenter: 'Jane Smith',
      talkDate: '2026-07-10',
      pdfUrl: '/docs/toolbox/TB-2026-0018.pdf',
      isRemote: false,
    } as ToolboxSignoffPayload,
  },
  // Plant Defect Alert
  {
    type: 'PLANT_DEFECT',
    priority: 'HIGH',
    employeeIds: ['e1', 'e5'],
    jobIds: ['j1'],
    heading: 'Plant defect — Cease use of EWP Boom #EWP-004',
    body: 'EWP Boom #EWP-004 has been marked defective: hydraulic leak detected at boom elbow joint. Do not operate. Asset removed from service.',
    requiredAction: 'Cease use immediately. Report to supervisor. Do not restart without PM authorisation.',
    sourceModule: 'PlantEquipment.tsx',
    sourceRecordId: 'asset-ewp004',
    requiresAck: true,
    payload: {
      assetId: 'asset-ewp004',
      assetName: 'EWP Boom (Snorkel TB60)',
      assetNumber: 'EWP-004',
      defectDescription: 'Hydraulic leak at boom elbow joint — oil pressure dropping under load',
      currentJobId: 'j1',
      currentJobNumber: 'TP-2026-001',
      ceaseUseInstruction: 'Do not operate. Isolate boom in low position. Report to PM.',
    } as PlantDefectPayload,
  },
  // Materials Ready
  {
    type: 'MATERIAL_READY',
    priority: 'MEDIUM',
    employeeIds: ['e7'],
    jobIds: ['j2'],
    heading: 'Materials confirmed on site — LED Signal Heads',
    body: 'LED Signal Head 3-aspect × 24 units have been received and confirmed at TP-2026-002 site. Your scheduled task (Signal Head Mounting) can now proceed.',
    sourceModule: 'PurchaseOrders.tsx',
    sourceRecordId: 'grn-0042',
    requiresAck: false,
    payload: {
      bomLineId: 'bom-j2-004',
      materialDescription: 'LED Signal Head 3-aspect, 300mm, TMR approved',
      quantity: 24,
      unit: 'EA',
      stockStatus: 'ON_SITE',
      linkedTaskId: 'ct005',
      linkedTaskTitle: 'Signal head installation — Entry point A northbound',
    } as MaterialReadyPayload,
  },
  // ITP Hold Point Release
  {
    type: 'ITP_RELEASE',
    priority: 'HIGH',
    employeeIds: ['e1'],
    jobIds: ['j1'],
    heading: 'Hold point released — HP-001 Concrete Pour Witness',
    body: 'ITP Hold Point HP-001 (Concrete Pour Witness) has been released by Jane Smith at 10:32am. You may proceed with formwork removal.',
    requiredAction: 'Acknowledge and proceed with formwork removal per Work Instruction WI-J1-003.',
    sourceModule: 'ITP.tsx',
    sourceRecordId: 'itp-hp-001',
    requiresAck: true,
    payload: {
      holdPointRef: 'HP-001',
      holdPointDescription: 'Concrete Pour Witness — Inspector must witness pour',
      activityReleased: 'Formwork removal and curing',
      releasedBy: 'Jane Smith (B_PM)',
      releasedAt: `${TODAY}T00:32:00Z`,
    } as ITPReleasePayload,
  },
  // Timesheet Approved
  {
    type: 'TIMESHEET_APPROVED',
    priority: 'LOW',
    employeeIds: ['e1'],
    jobIds: ['j1'],
    heading: 'Timesheet approved — week ending 4 July 2026',
    body: 'Your timesheet for the week ending 4 July 2026 has been approved by Sarah Lee. 43.5 hours approved. 2.0 hours adjusted by PM (overtime Saturday reclassified).',
    sourceModule: 'Timesheets.tsx',
    sourceRecordId: 'ts-e1-2026-w27',
    requiresAck: false,
    payload: {
      payPeriodLabel: 'Week ending 4 July 2026',
      payPeriodStart: '2026-06-28',
      payPeriodEnd: '2026-07-04',
      ordinaryHours: 40,
      overtimeHours: 3.5,
      nightLoadingHours: 0,
      grossEstimate: 2840,
      adjustmentsMade: true,
      adjustmentReason: 'Saturday hours reclassified as OT@1.5x per award',
    } as TimesheetApprovedPayload,
  },
  // Incident Alert (auto)
  {
    type: 'INCIDENT_ALERT',
    priority: 'HIGH',
    employeeIds: SEED_EMPLOYEE_IDS,
    jobIds: ['j2'],
    heading: 'WHS Incident — Pacific Mwy On-ramp site',
    body: 'HIGH severity near-miss reported at TP-2026-002. Delivery truck entered exclusion zone while crew were in Pit 2B. No injury. Site review underway.',
    requiredAction: 'Do not enter Pit 2B until PM gives clearance. Attend briefing at site office at 2pm.',
    sourceModule: 'Incidents.tsx',
    sourceRecordId: 'whs003',
    requiresAck: true,
    payload: {
      incidentRef: 'WHS-TP002-0003',
      incidentType: 'Near-miss',
      severity: 'HIGH',
      location: 'Pacific Motorway on-ramp, Eight Mile Plains',
      initialActions: 'Pit 2B isolated. CCTV review requested.',
      investigationStatus: 'UNDER_REVIEW',
    } as IncidentAlertPayload,
  },
]

// Seed the store at module load
seedData.forEach(s => addCrewNotification(s))

// ── Reset (for testing) ────────────────────────────────────────────
export function _resetNotificationBus(): void {
  _crewNotifications = []
  _seq = 100
  seedData.forEach(s => addCrewNotification(s))
}
